/**
 * The client's string layer (architecture §9.3, ADR-057).
 *
 * §9.3 states the rule twice — **"no player-visible string anywhere in code"** — and
 * §9.2 calls the CI walk that enforces it *"the load-bearing one under ADR-057's 'no
 * rework in M2' claim"*: it fails *"not only on a missing translation but on a
 * **literal** — an English sentence written straight into a component."* That walk
 * lives in `test/i18n-call-sites.test.ts`; this module is the `t()` it walks to.
 *
 * **Shape.** Flat, semantic, English stable ids (`ui.tray.roll`, `ui.hud.present`,
 * `ui.period.dawn`), one JSON catalog per locale, ICU message syntax in the values.
 * v1 ships `en-US` only; M2 adds `pt-BR.json` against the same keys and registers it
 * below — a catalog and a voice, not a refactor (§12.6).
 *
 * **Fallback.** A miss falls back to the `en-US` value, exactly as `LocalizedString`
 * does on the wire (§5.1: "`LocalizedString {en, pt}` … M2 fills a field rather than
 * migrating a dataset"). A miss in `en-US` itself is a bug, so it throws in dev and
 * returns the id in production rather than rendering an empty node.
 *
 * **ICU subset.** `{arg}`, `{arg, plural, =0 {…} one {…} other {…}}` (with `#`) and
 * `{arg, select, key {…} other {…}}` are supported — enough for every message the v1
 * catalog holds, and the same syntax a full formatter (`@formatjs/intl-messageformat`)
 * accepts, so M2 can swap the formatter without touching a single id or catalog value.
 * Anything richer belongs in the catalog as a new id, not in a component.
 */

// `resolveJsonModule` (tsconfig.base.json) types this as a string map; Vite and
// vitest both inline it at build time.
import { metresFromFeet, type Measures } from '@everwake/protocol';
import enUS from './en-US.json';
import ptBR from './pt-BR.json';

export type CatalogLocale = 'en-US' | 'pt-BR';

export type Catalog = Readonly<Record<string, string>>;

export type MessageParams = Readonly<Record<string, string | number>>;

/** The locale whose catalog v1 actually ships (ADR-057). */
export const DEFAULT_LOCALE: CatalogLocale = 'en-US';

/**
 * Registered catalogs. `pt-BR` is deliberately absent: M2 adds the file and one entry
 * here, and `t()` starts resolving it with the `en-US` value as the fallback.
 */
/* ADR-104: Brazilian Portuguese is a language the platform speaks. A key the pt-BR
 * catalogue is missing falls back to English, as `LocalizedString` does (§5.1). */
const CATALOGS: Partial<Record<CatalogLocale, Catalog>> = {
  'en-US': enUS as Catalog,
  'pt-BR': ptBR as Catalog,
};

let activeLocale: CatalogLocale = DEFAULT_LOCALE;

/** The session locale drives this; v1 never calls it with anything but `en-US`. */
export function setLocale(locale: CatalogLocale): void {
  activeLocale = locale;
  try {
    window.localStorage.setItem(LOCALE_KEY, locale);
  } catch {
    /* A browser that refuses storage still plays; the choice lasts the session. */
  }
}

const LOCALE_KEY = 'everwake.locale';

/** The language this browser chose, else the one it asks for, else English. */
export function preferredLocale(): CatalogLocale {
  try {
    const kept = window.localStorage.getItem(LOCALE_KEY);
    if (kept === 'pt-BR' || kept === 'en-US') return kept;
  } catch {
    /* fall through to the browser's own preference */
  }
  const asked = typeof navigator === 'undefined' ? '' : (navigator.language ?? '');
  return asked.toLowerCase().startsWith('pt') ? 'pt-BR' : 'en-US';
}

export function getLocale(): CatalogLocale {
  return activeLocale;
}

/** Every id the shipped catalog declares — what the CI call-site walk checks against. */
export function catalogKeys(locale: CatalogLocale = DEFAULT_LOCALE): readonly string[] {
  return Object.keys(CATALOGS[locale] ?? {});
}

function lookup(id: string): string | undefined {
  const active = CATALOGS[activeLocale];
  const hit = active?.[id];
  if (hit !== undefined) return hit;
  // Fallback to the English value, the same way `LocalizedString` falls back (§5.1).
  return CATALOGS[DEFAULT_LOCALE]?.[id];
}

/**
 * Resolve a catalog id to display text.
 *
 * @param id     A flat catalog id. Must be a string literal at the call site so the
 *               CI walk can prove it exists in the declared catalog (§9.2).
 * @param params ICU arguments referenced by the message.
 */
export function t(id: string, params: MessageParams = {}): string {
  const message = lookup(id);
  if (message === undefined) {
    if (import.meta.env?.DEV) {
      throw new Error(`i18n: missing catalog id "${id}" in ${activeLocale} and ${DEFAULT_LOCALE}`);
    }
    return id;
  }
  return formatIcu(message, params);
}

/* -------------------------------------------------------------- ICU subset */

/**
 * Split a message into its top-level literal and `{…}` segments, respecting nesting.
 * Returns the formatted string.
 */
export function formatIcu(message: string, params: MessageParams): string {
  let out = '';
  let i = 0;
  while (i < message.length) {
    const ch = message[i];
    if (ch !== '{') {
      out += ch;
      i += 1;
      continue;
    }
    const end = matchBrace(message, i);
    if (end === -1) {
      // Unbalanced brace: emit the rest verbatim rather than silently truncating.
      out += message.slice(i);
      break;
    }
    out += formatArgument(message.slice(i + 1, end), params);
    i = end + 1;
  }
  return out;
}

/** Index of the `}` closing the `{` at `open`, or -1 when the message is unbalanced. */
function matchBrace(message: string, open: number): number {
  let depth = 0;
  for (let i = open; i < message.length; i += 1) {
    if (message[i] === '{') depth += 1;
    else if (message[i] === '}') {
      depth -= 1;
      if (depth === 0) return i;
    }
  }
  return -1;
}

function formatArgument(body: string, params: MessageParams): string {
  const firstComma = body.indexOf(',');
  if (firstComma === -1) {
    const value = params[body.trim()];
    return value === undefined ? '' : String(value);
  }

  const name = body.slice(0, firstComma).trim();
  const rest = body.slice(firstComma + 1).trimStart();
  const secondComma = rest.indexOf(',');
  const type = (secondComma === -1 ? rest : rest.slice(0, secondComma)).trim();
  const options = secondComma === -1 ? '' : rest.slice(secondComma + 1);
  const value = params[name];

  if (type !== 'plural' && type !== 'select') {
    // `{n, number}` and friends: no formatter in the v1 subset, print the value.
    return value === undefined ? '' : String(value);
  }

  const branches = parseBranches(options);
  const key = type === 'plural' ? pluralKey(value, branches) : String(value ?? '');
  const chosen = branches.get(key) ?? branches.get('other') ?? '';
  const resolved = formatIcu(chosen, params);
  return type === 'plural' ? resolved.replaceAll('#', String(value ?? '')) : resolved;
}

/** `=0 {…} one {…} other {…}` → Map. */
function parseBranches(options: string): Map<string, string> {
  const branches = new Map<string, string>();
  let i = 0;
  while (i < options.length) {
    while (i < options.length && /\s/.test(options[i] as string)) i += 1;
    const keyStart = i;
    while (i < options.length && options[i] !== '{' && !/\s/.test(options[i] as string)) i += 1;
    const key = options.slice(keyStart, i);
    while (i < options.length && /\s/.test(options[i] as string)) i += 1;
    if (options[i] !== '{') break;
    const end = matchBrace(options, i);
    if (end === -1) break;
    branches.set(key, options.slice(i + 1, end));
    i = end + 1;
  }
  return branches;
}

/** The v1 subset's plural selector: exact `=n` matches, then English `one`/`other`. */
function pluralKey(value: string | number | undefined, branches: Map<string, string>): string {
  const n = typeof value === 'number' ? value : Number(value);
  if (Number.isFinite(n) && branches.has(`=${n}`)) return `=${n}`;
  return n === 1 ? 'one' : 'other';
}

/**
 * ADR-119: a distance as this table reads it. The protocol does the arithmetic and the
 * unit; this puts it in the reader's own number format, which is the one thing a shared
 * helper cannot know.
 */
export function distance(feet: number, measures: Measures): string {
  if (measures === 'feet') return t('ui.units.feet', { n: Math.round(feet) });
  const metres = metresFromFeet(feet);
  return t('ui.units.metres', { n: new Intl.NumberFormat(getLocale(), { maximumFractionDigits: 1 }).format(metres) });
}
